import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  Card,
  Pagination,
  notification,
  Input,
  Tooltip,
} from "antd";
import { useForm } from "react-hook-form";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { addressManagementSaga } from "./saga";
import * as selectors from "./selectors";
import { addressToStringMultiLine } from "../../../../utils/formatter";
import AccountSidebar from "../../../../components/AccountSidebar";
import { useAuth } from "../../../../contexts/auth";
import PageHeader from "../../../../components/PageHeader";
import UserAddressForm from "../../../../forms/UserAddressForm";
import addressSchema from "../../../../schemas/addressSchema";
import Loader from "../../../../components/Loader";
import "./style.css";

const AddressCard = ({ title, address, options, editable }) => {
  const [showForm, setShowForm] = useState(false);
  const dispatch = useDispatch();
  const auth = useAuth();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...address,
      city: address?.city?._id,
      state: address?.state?._id,
      country: address?.country?._id,
    },
    resolver: yupResolver(addressSchema),
  });

  const handleCancel = () => {
    reset();
    setShowForm(false);
  };

  const handleSuccess = () => {
    reset();
    setShowForm(false);
    auth.fetchLoggedInUser();
  };

  const onSubmit = (data) => {
    const payload = {
      id: data._id,
      data,
      onSuccess: handleSuccess,
    };
    dispatch(actions.updateAddress(payload));
  };

  return (
    <Card
      title={title}
      className="addressCard"
      actions={
        editable
          ? [
              <Button icon={<EditOutlined />} onClick={() => setShowForm(true)}>
                Edit
              </Button>,
            ]
          : []
      }
    >
      <div style={{ minHeight: 120 }}>{addressToStringMultiLine(address)}</div>
      <Modal
        title="Edit Address"
        visible={showForm}
        centered
        onOk={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        okText="Update"
      >
        <UserAddressForm
          errors={errors}
          control={control}
          watch={watch}
          options={options}
        />
      </Modal>
    </Card>
  );
};

const AddressManagementPage = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: addressManagementSaga });
  const auth = useAuth();
  const user = auth.loggedInUser;
  const company = user.company;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actions.fetchCountries());
    dispatch(
      actions.fetchPickupAddress({ userId: user._id, page: 1, pageSize: 6 })
    );
    dispatch(
      actions.fetchBillingAddress({ userId: user._id, page: 1, pageSize: 6 })
    );
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, user]);

  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  const pickupAddress = useSelector(selectors.selectPickupAddress) || [];
  const pickupAddressCount =
    useSelector(selectors.selectPickupAddressCount) || 0;
  const billingAddress = useSelector(selectors.selectBillingAddress) || [];
  const billingAddressCount =
    useSelector(selectors.selectBillingAddressCount) || 0;
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  const [searchPickupAddress, setSearchPickupAddress] = useState(null);
  const [searchBillingAddress, setSearchBillingAddress] = useState("");

  const [pageCountPickupAddress, setPageCountPickupAddress] = useState(1);
  const [pageCountBillingAddress, setPageCountBillingAddress] = useState(1);
  const [searchPickupActive, setSearchPickupActive] = useState(false);
  const [searchBillingActive, setSearchBillingActive] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Message",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  const handlePickupAddressPageChange = (page) => {
    setPageCountPickupAddress(page);
    if (searchPickupAddress)
      dispatch(
        actions.fetchPickupAddress({
          userId: user._id,
          page: page,
          pageSize: 6,
          name: searchPickupAddress,
        })
      );
    else
      dispatch(
        actions.fetchPickupAddress({
          userId: user._id,
          page: page,
          pageSize: 6,
        })
      );
  };

  const handlePickupAddressReset = () => {
    setSearchPickupAddress("");
    setSearchPickupActive(false);
    setPageCountPickupAddress(1);
    dispatch(
      actions.fetchPickupAddress({ userId: user._id, page: 1, pageSize: 6 })
    );
  };

  const handleSearchPickupAddress = () => {
    setSearchPickupActive(true);
    dispatch(
      actions.fetchPickupAddress({
        userId: user._id,
        page: 1,
        pageSize: 6,
        name: searchPickupAddress,
      })
    );
  };

  const handleBillingAddressPageChange = (page) => {
    setPageCountBillingAddress(page);
    if (searchBillingAddress)
      dispatch(
        actions.fetchBillingAddress({
          userId: user._id,
          page: page,
          pageSize: 6,
          name: searchBillingAddress,
        })
      );
    else
      dispatch(
        actions.fetchBillingAddress({
          userId: user._id,
          page: page,
          pageSize: 6,
        })
      );
  };

  const handleBillingAddressReset = () => {
    setSearchBillingAddress("");
    setPageCountBillingAddress(1);
    setSearchBillingActive(false);
    dispatch(
      actions.fetchBillingAddress({ userId: user._id, page: 1, pageSize: 6 })
    );
  };

  const handleSearchBillingAddress = () => {
    setSearchBillingActive(true);
    dispatch(
      actions.fetchBillingAddress({
        userId: user._id,
        page: 1,
        pageSize: 6,
        name: searchBillingAddress,
      })
    );
  };
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      type: "pickup",
      addressLine1: "",
      addressLine2: "",
      district: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      landmark: "",
    },
    resolver: yupResolver(addressSchema),
  });

  const selectedCountry = watch("country");
  const selectedState = watch("state");

  useEffect(() => {
    if (selectedCountry)
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState)
      dispatch(actions.fetchCities({ stateId: selectedState }));
  }, [selectedState, dispatch]);

  const handleCancel = () => {
    reset();
    setShowAddAddress(false);
  };

  const handleSuccess = () => {
    reset();
    notification.success({
      message: "Address added successfully",
    });
    setShowAddAddress(false);
    auth.fetchLoggedInUser();
  };

  const onSubmit = (data) => {
    const payload = {
      id: company._id,
      data,
      onSuccess: handleSuccess,
    };
    dispatch(actions.addAddress(payload));
  };


  useLayoutEffect(() => {
    function updateWindowSize() {
      setWindowSize(window.innerWidth);
    }
    window.addEventListener("resize", updateWindowSize);
    updateWindowSize();
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  if (loading) return <Loader />;
  else {
    return (
      <div className="addressManagementPageContainer">
        <AccountSidebar />
        <div style={{ flex: 1 }}>
          <PageHeader
            title="Saved Addresses"
            actions={
              company.allowUserPickupAddress ? (

                <Button
                  type="primary"
                  size={
                    windowSize > 600
                      ? "large"
                      : windowSize > 500
                      ? "middle"
                      : "small"
                  }
                  onClick={() => setShowAddAddress(true)}
                >
                  Add Pickup Address
                </Button>
              ) : (
                ""
              )
            }
          />


          <p className="addressLabel">
            Pickup Addresses ({pickupAddressCount})
          </p>
          <div style={{ display: "flex" }}>
            {" "}
            <div className="searchBox">
              <Input
                value={searchPickupAddress}
                onChange={(e) => {
                  setSearchPickupAddress(e.target.value);
                }}

                className="inputBox"
              />
              <Tooltip title="Search Pickup Address">
                {" "}
                <Button
                  style={{ marginLeft: "10px" }}
                  onClick={handleSearchPickupAddress}

                  size={windowSize < 550 ? "small" : "middle"}
                >
                  <SearchOutlined />
                </Button>
              </Tooltip>
            </div>
            <div>

              <Button
                onClick={handlePickupAddressReset}
                size={windowSize < 550 ? "small" : "middle"}
              >
                Reset
              </Button>
            </div>
          </div>
          {pickupAddress.length > 0 ? (
            <div style={{ marginTop: "10px" }}>
              <div className="largerScreens">
                <Row gutter={[32, 32]}>
                  {pickupAddress.map((addr) => (
                    <Col key={addr._id}>
                      <AddressCard
                        title="Pickup Address"
                        address={addr}
                        editable={false}
                        options={{ countries, states, cities }}
                      />
                    </Col>
                  ))}
                </Row>

              </div>
              <div className="smallerScreens">
                {" "}
                {pickupAddress.map((addr) => (
                  <div className="smallerScreensCard">
                    <AddressCard
                      title="Pickup Address"
                      address={addr}
                      editable={false}
                      options={{ countries, states, cities }}
                    />
                  </div>
                ))}
              </div>
              {pickupAddressCount > 6 && (
                <div style={{ float: "right", marginTop: "10px" }}>
                  <Pagination
                    current={pageCountPickupAddress}
                    pageSize={6}
                    showSizeChanger={false}
                    total={pickupAddressCount}
                    onChange={handlePickupAddressPageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: "18px", color: "red" }}>
              {searchPickupActive && <p>No Pickup Address Found</p>}
            </div>
          )}

          <div style={{ marginTop: 30 }}>
            <p className="addressLabel">
              Billing Addresses ({billingAddressCount})
            </p>
            <div style={{ display: "flex" }}>
              <div className="searchBox">
                <Input
                  value={searchBillingAddress}
                  onChange={(e) => {
                    setSearchBillingAddress(e.target.value);
                  }}
                  className="inputBox"
                />
                <Tooltip title="Search Billing Address">
                  {" "}
                  <Button
                    style={{ marginLeft: "10px" }}
                    onClick={handleSearchBillingAddress}
                    size={windowSize < 550 ? "small" : "middle"}
                  >
                    <SearchOutlined />
                  </Button>
                </Tooltip>
              </div>
              <div>
                <Button
                  onClick={handleBillingAddressReset}
                  size={windowSize < 550 ? "small" : "middle"}
                >
                  Reset
                </Button>

              </div>
            </div>
            {billingAddress.length > 0 ? (
              <div style={{ marginTop: "10px" }}>
                <div className="largerScreens">
                  <Row gutter={[32, 32]}>
                    {billingAddress.map((addr) => (
                      <Col key={addr._id}>
                        <AddressCard
                          editable={false}
                          title="Billing Address"
                          address={addr}
                          options={{ countries, states, cities }}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
                <div className="smallerScreens">
                  {billingAddress.map((addr) => (
                    <AddressCard
                      editable={false}
                      title="Billing Address"
                      address={addr}
                      options={{ countries, states, cities }}
                    />
                  ))}
                </div>
                {billingAddressCount > 6 && (
                  <div style={{ float: "right", marginTop: "10px" }}>
                    <Pagination
                      current={pageCountBillingAddress}
                      pageSize={6}
                      showSizeChanger={false}
                      total={billingAddressCount}
                      onChange={handleBillingAddressPageChange}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: "red", fontSize: "18px" }}>
                {searchBillingActive && <p>No Billing Address Found</p>}
              </div>
            )}
          </div>
        </div>
        <Modal
          title="Add Pickup Address"
          visible={showAddAddress}
          centered
          onOk={handleSubmit(onSubmit)}
          onCancel={handleCancel}
          okText="Save"
        >
          <UserAddressForm
            errors={errors}
            control={control}
            watch={watch}
            options={{ countries, states, cities }}
            setValue={setValue}
          />
        </Modal>
      </div>
    );
  }
};

export default AddressManagementPage;
